import { BridgeStatus } from '@/lib/features/Assets/interfaces'
import { REFERENCE_MAX_LENGTH } from '@/lib/features/SmartShares/constants'
import { ShareReferencePatchData } from '@/lib/features/SmartShares/interfaces'

import messages from './EditableReference.messages'
import { EditableReferenceShare } from './EditableReference.types'

/**
 * A reference may only be edited on shares the current organisation issued. Inbound (`BORROWED`)
 * shares belong to the sharing organisation, so their reference is read-only here. Onward shares
 * created by the current organisation are `LENT` and therefore editable.
 */
export const canEditReference = (bridgeStatus: BridgeStatus) => bridgeStatus === BridgeStatus.LENT

/** Trims surrounding whitespace. A blank reference is a valid value — it clears the field. */
export const normalizeReference = (value: string | null | undefined) => (value ?? '').trim()

export const hasReferenceChanged = (
  original: string | null | undefined,
  next: string | null | undefined,
) => normalizeReference(original) !== normalizeReference(next)

/** Returns a validation message, or `null` when the value may be saved. */
export const validateReference = (value: string | null | undefined) =>
  normalizeReference(value).length > REFERENCE_MAX_LENGTH
    ? messages.maxLengthValidation.defaultMessage
    : null

/** Placeholder shown in the table when a share has no reference. */
export const formatReferenceForDisplay = (value: string | null | undefined) =>
  normalizeReference(value) || '-'

/**
 * Builds the PATCH body for a reference edit. The endpoint re-validates the whole share rather than
 * applying a partial update, so every field it checks is echoed back exactly as the API returned it
 * — dates in particular are passed through untouched so the share period cannot drift.
 */
export const buildReferencePatchPayload = (
  share: EditableReferenceShare,
  reference: string | null | undefined,
): ShareReferencePatchData => ({
  asset: share.assetId,
  dateFrom: share.dateFrom,
  dateTo: share.dateTo,
  organizationTo: share.organization.id,
  contactTo: share.contactTo?.id ?? null,
  contactFrom: share.contactFrom?.id ?? null,
  reference: normalizeReference(reference),
})

/**
 * Pulls the first human-readable message out of a parsed API error body.
 *
 * The shape varies: DRF returns bare arrays (`["Invalid date format for 'date_from'..."]`), field
 * maps (`{ dateFrom: ['...'] }`) and `{ detail: '...' }`, and `RequestWrapper.parseError` spreads
 * arrays into objects with numeric keys before we ever see them. Walking the structure for its first
 * non-empty string handles all of those without guessing at a single shape.
 */
export const extractApiErrorMessage = (payload: unknown): string | null => {
  if (typeof payload === 'string') {
    return payload.trim() || null
  }

  if (Array.isArray(payload)) {
    for (const item of payload) {
      const message = extractApiErrorMessage(item)
      if (message) return message
    }
    return null
  }

  if (payload && typeof payload === 'object') {
    for (const value of Object.values(payload)) {
      const message = extractApiErrorMessage(value)
      if (message) return message
    }
  }

  return null
}
